import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  GraduationCap, Search, Clock, ChevronRight, PlayCircle, Award, CheckCircle2, Sparkles,
} from "lucide-react";

const LEVEL_LABEL = { iniciante: "Iniciante", intermediario: "Intermediário", avancado: "Avançado" };

export default function Courses() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");

  useEffect(() => {
    api.get("/courses").then(({ data }) => setItems(data)).catch(() => setItems([]));
  }, [user?.id]);

  const categories = useMemo(() => {
    const s = new Set();
    (items || []).forEach((c) => c.category && s.add(c.category));
    return ["all", ...Array.from(s).sort()];
  }, [items]);

  const filtered = (items || []).filter((c) => {
    if (category !== "all" && c.category !== category) return false;
    const t = q.trim().toLowerCase();
    if (!t) return true;
    return c.title?.toLowerCase().includes(t) || c.description?.toLowerCase().includes(t);
  });

  return (
    <div>
      <section className="relative overflow-hidden border-b border-[hsl(var(--border))] bg-gradient-to-br from-[hsl(var(--secondary))]/8 via-white to-[hsl(var(--primary))]/8">
        <div className="container-cuidar py-14">
          <div className="text-xs font-bold uppercase tracking-widest text-[hsl(var(--secondary))] inline-flex items-center gap-2">
            <Sparkles className="h-3.5 w-3.5" /> Cuidar Educacional
          </div>
          <h1 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-black leading-tight max-w-3xl">
            Cursos para pais, cuidadores e famílias neuroatípicas
          </h1>
          <p className="mt-5 max-w-2xl text-[hsl(var(--muted-foreground))]">
            Aprenda no seu ritmo, do zero ao aprofundamento. Cada curso tem módulos curtos, quiz
            ao final e certificado emitido em seu nome (R$ 15 para baixar o certificado impresso).
          </p>
          <div className="mt-8 flex flex-col md:flex-row gap-3">
            <div className="relative flex-1 max-w-lg">
              <Search className="h-4 w-4 absolute left-4 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por curso ou palavra-chave…"
                className="h-12 pl-10 rounded-full border-[hsl(var(--border))] bg-white shadow-sm"
                data-testid="courses-search-input" />
            </div>
            <div className="flex gap-1 flex-wrap">
              {categories.map((c) => (
                <button key={c} onClick={() => setCategory(c)}
                  className={`text-sm px-4 h-10 rounded-full border transition-colors ${
                    category === c
                      ? "bg-[hsl(var(--primary))] border-[hsl(var(--primary))] text-white"
                      : "bg-white border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]/50"
                  }`}
                  data-testid={`courses-category-${c}`}>
                  {c === "all" ? "Todos" : c}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="container-cuidar py-14">
        {items === null && (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (<div key={i} className="surface p-6 animate-pulse h-64 bg-[hsl(var(--muted))]" />))}
          </div>
        )}
        {items !== null && filtered.length === 0 && (
          <div className="surface p-10 text-center" data-testid="courses-empty">
            <GraduationCap className="h-10 w-10 text-[hsl(var(--muted-foreground))] mx-auto" />
            <div className="mt-3 font-heading font-bold">Nenhum curso encontrado</div>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Tente ajustar a busca ou o filtro por categoria.</p>
          </div>
        )}
        {items !== null && filtered.length > 0 && (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="courses-grid">
            {filtered.map((c, idx) => (
              <Link key={c.id} to={`/cursos/${c.slug}`} className="group surface p-0 overflow-hidden hover:-translate-y-1 hover:shadow-lg transition-all flex flex-col"
                data-testid={`course-card-${c.slug}`}
                style={{ animation: `fadeIn 0.4s ${idx * 50}ms both` }}>
                <div className="relative h-40 bg-gradient-to-br from-[hsl(var(--primary))]/80 to-[hsl(var(--primary))]/60 grid place-items-center overflow-hidden">
                  {c.cover_image_url ? (
                    <img src={c.cover_image_url} alt={c.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  ) : (
                    <GraduationCap className="h-16 w-16 text-white/60 group-hover:scale-110 transition-transform" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />
                  <div className="absolute top-3 left-3">
                    <Badge className="bg-white/95 text-[hsl(var(--primary))] hover:bg-white rounded-full">{c.category}</Badge>
                  </div>
                  <div className="absolute top-3 right-3">
                    <Badge variant="outline" className="bg-white/95 rounded-full text-[hsl(var(--foreground))]">
                      {LEVEL_LABEL[c.level] || c.level}
                    </Badge>
                  </div>
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <div className="font-heading font-extrabold text-lg leading-tight" data-testid={`course-title-${c.slug}`}>{c.title}</div>
                  <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))] line-clamp-3 flex-1">{c.description}</p>
                  <div className="mt-4 flex items-center gap-4 text-xs text-[hsl(var(--muted-foreground))]">
                    <div className="flex items-center gap-1"><PlayCircle className="h-3.5 w-3.5" /> {c.modules_count} módulos</div>
                    {c.estimated_hours ? (
                      <div className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {c.estimated_hours}h</div>
                    ) : null}
                    <div className="flex items-center gap-1 text-[hsl(var(--secondary))]"><Award className="h-3.5 w-3.5" /> Certificado</div>
                  </div>
                  {c.enrolled && (
                    <div className="mt-4">
                      <Progress value={c.progress_percent} className="h-2" />
                      <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1 flex justify-between">
                        <span>Matriculado</span>
                        <span className="font-semibold text-[hsl(var(--primary))]">
                          {c.completed ? <><CheckCircle2 className="h-3 w-3 inline" /> Concluído</> : `${c.progress_percent}%`}
                        </span>
                      </div>
                    </div>
                  )}
                  <div className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-[hsl(var(--primary))]">
                    {c.enrolled ? "Continuar" : "Ver detalhes"} <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {user && (
          <div className="mt-14 rounded-3xl bg-[hsl(var(--primary))]/8 p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-widest text-[hsl(var(--primary))]">Sua jornada</div>
              <h3 className="mt-2 font-heading text-2xl font-black">Veja o progresso dos seus cursos</h3>
              <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1 max-w-xl">
                No painel do Associado você acompanha módulos concluídos, quizzes e baixa seus certificados.
              </p>
            </div>
            <Button onClick={() => navigate("/associado")} className="btn-pill bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90 h-12 px-6 shrink-0" data-testid="courses-goto-dashboard-btn">
              Ir para o painel <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        )}
      </section>
    </div>
  );
}
